import { useState } from 'react'
import {
  IMPACT_LEVELS,
  URGENCY_LEVELS,
  PRIORITY_MATRIX,
  getPriorityMeta,
} from '../../utils/priorityMatrix'

/** Ejes de la ayuda: comparten estructura, así que se renderizan igual. */
const AXES = [
  {
    key: 'impact',
    label: 'Impacto',
    icon: 'fa-users',
    question: '¿A cuántas personas o qué tan crítico es lo afectado?',
    levels: IMPACT_LEVELS,
  },
  {
    key: 'urgency',
    label: 'Urgencia',
    icon: 'fa-stopwatch',
    question: '¿Qué tan pronto se necesita resolver?',
    levels: URGENCY_LEVELS,
  },
]

/**
 * Guía de impacto y urgencia. Explica cada eje con ejemplos y muestra la matriz
 * real del sistema (`PRIORITY_MATRIX`), de modo que la ayuda nunca se
 * desincroniza de la prioridad que se calcula al guardar.
 *
 * Está pensada para renderizarse dentro de `TicketHelpPanel`, que aporta los
 * estilos compartidos `.hg-*`.
 *
 * @component
 * @returns {JSX.Element}
 */
const PriorityGuide = () => {
  const [axisKey, setAxisKey] = useState('impact')
  /** Nivel expandido dentro del eje activo. */
  const [openLevel, setOpenLevel] = useState(null)

  const axis = AXES.find((a) => a.key === axisKey) || AXES[0]

  const selectAxis = (key) => {
    setAxisKey(key)
    setOpenLevel(null)
  }

  const toggle = (value) =>
    setOpenLevel((prev) => (prev === value ? null : value))

  return (
    <div className="pg">
      <p className="hg-intro">
        No elija la prioridad: el sistema la calcula. Usted indica{' '}
        <strong>a cuántos afecta</strong> y{' '}
        <strong>qué tan pronto lo necesita</strong>.
      </p>

      <div className="hg-seg">
        {AXES.map((a) => {
          const active = a.key === axisKey
          return (
            <button
              key={a.key}
              type="button"
              className={`hg-seg-btn ${active ? 'is-active' : ''}`}
              onClick={() => selectAxis(a.key)}
              style={
                active
                  ? {
                      color: 'var(--gm-primary)',
                      borderColor: 'var(--gm-primary)',
                      background: 'var(--gm-primary-soft)',
                    }
                  : undefined
              }
            >
              <i className={`fa-solid ${a.icon}`} />
              {a.label}
            </button>
          )
        })}
      </div>

      <p className="pg-question">{axis.question}</p>

      <div className="hg-rows">
        {axis.levels.map((lvl) => {
          const isOpen = openLevel === lvl.value
          return (
            <div
              key={lvl.value}
              className="hg-row"
              style={
                isOpen
                  ? {
                      borderColor: `${lvl.color}66`,
                      background: `${lvl.color}0d`,
                    }
                  : undefined
              }
            >
              <button
                type="button"
                className="hg-row-btn"
                onClick={() => toggle(lvl.value)}
                aria-expanded={isOpen}
              >
                <span
                  className="pg-dot"
                  style={{ background: lvl.color }}
                  aria-hidden="true"
                />
                <span className="hg-row-texts">
                  <span className="hg-row-label">{lvl.label}</span>
                  <span className="hg-row-hint">{lvl.hint}</span>
                </span>
                <i
                  className={`fa-solid fa-chevron-down hg-chevron ${
                    isOpen ? 'is-open' : ''
                  }`}
                />
              </button>

              {isOpen && (
                <ul className="hg-examples pg-examples">
                  {lvl.examples.map((example) => (
                    <li key={example}>{example}</li>
                  ))}
                </ul>
              )}
            </div>
          )
        })}
      </div>

      {/* Matriz Impacto × Urgencia → Prioridad */}
      <div className="pg-matrix">
        <div className="pg-matrix-title">
          <i className="fa-solid fa-table-cells me-2" />
          Prioridad resultante
        </div>

        <div className="pg-grid">
          <span className="pg-corner">
            imp. <span className="pg-corner-sep">/</span> urg.
          </span>
          {URGENCY_LEVELS.map((u) => (
            <span key={u.value} className="pg-axis-label">
              {u.label}
            </span>
          ))}

          {/* Celdas planas: cada fila son 1 etiqueta + 3 celdas dentro del
              mismo grid, sin envoltorios que rompan la alineación. */}
          {IMPACT_LEVELS.flatMap((i) => [
            <span key={`h-${i.value}`} className="pg-axis-label pg-axis-row">
              {i.label}
            </span>,
            ...URGENCY_LEVELS.map((u) => {
              const meta = getPriorityMeta(PRIORITY_MATRIX[i.value][u.value])
              return (
                <span
                  key={`${i.value}-${u.value}`}
                  className="pg-cell"
                  style={{ color: meta.color, background: `${meta.color}1a` }}
                >
                  {meta.label}
                </span>
              )
            }),
          ])}
        </div>

        <p className="pg-note">
          Filas: impacto · Columnas: urgencia. Un impacto alto con urgencia baja
          no es crítico: se atiende, pero programado.
        </p>
      </div>

      <style>{`
        .pg-question {
          margin: -6px 0 10px;
          font-size: 0.72rem;
          line-height: 1.35;
          color: var(--gm-text-faint);
        }

        /* El punto de color reemplaza al icono: el nivel se distingue por
           severidad, no por símbolo. */
        .pg-dot {
          width: 10px;
          height: 10px;
          flex-shrink: 0;
          margin: 0 8px;
          border-radius: 50%;
        }
        .pg-examples { padding-left: 36px; }

        .pg-matrix { margin-top: 14px; }
        .pg-matrix-title {
          display: flex;
          align-items: center;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: var(--gm-text-muted);
          margin-bottom: 8px;
        }
        .pg-matrix-title i { color: var(--gm-primary); }

        /* Grid en lugar de <table>: se comprime mejor en la barra lateral */
        .pg-grid {
          display: grid;
          grid-template-columns: minmax(42px, 0.8fr) repeat(3, 1fr);
          gap: 4px;
          align-items: center;
        }
        .pg-corner {
          font-size: 0.6rem;
          line-height: 1.1;
          color: var(--gm-text-faint);
        }
        .pg-corner-sep { opacity: 0.6; }
        .pg-axis-label {
          font-size: 0.66rem;
          font-weight: 600;
          color: var(--gm-text-muted);
          text-align: center;
        }
        .pg-axis-row { text-align: left; }

        .pg-cell {
          display: block;
          font-size: 0.66rem;
          font-weight: 600;
          text-align: center;
          padding: 6px 2px;
          border-radius: 7px;
        }

        .pg-note {
          margin: 10px 0 0;
          font-size: 0.7rem;
          line-height: 1.35;
          color: var(--gm-text-faint);
        }
      `}</style>
    </div>
  )
}

export default PriorityGuide
